import { useEffect, useState, useCallback, useRef } from 'react';
import dayjs from 'dayjs';

import AddInventory from '../../components/inventory/add/add-inventory.component';
import Button from '../../components/reusable/button/button.component';
import ComponentModal from '../../components/reusable/component-modal/component-modal.component';
import Spinner from '../../components/reusable/spinner/spinner.component';
import Pagination from '../../components/reusable/pagination/pagination.component';
import InventoriesWrapper from '../../components/inventory/inventories-wrapper/inventories-wrapper.component';
import TableControls from '../../components/reusable/table-controls/table-controls.component';

import useTableState from '../../hooks/useTableState';
import Client from '../../tools/client';

import {
    ContentContainer,
    MainTitle,
    TabContainer,
    TabSelector
} from '../../styles/page.styles';

const client = new Client();

const InventoriesPage = () => {
    const [loading, setLoading] = useState(true);
    const [inventories, setInventories] = useState([]);
    const [inventoriesCount, setInventoriesCount] = useState(null);
    const [showAddInventory, setShowAddInventory] = useState(false);

    const {
        page,
        size,
        search,
        sortKey,
        sortDirection,
        setPage,
        setSize,
        setSearch,
        setSortKey,
        setSortDirection
    } = useTableState({ defaultSize: 25 });

    const isInitialMount = useRef(true);

    const getInventories = useCallback(async () => {
        setLoading(true);
        let query = `?page=${page}&size=${size}`;
        if (search) query += `&search=${encodeURIComponent(search)}`;
        if (sortDirection) query += `&sortDirection=${sortDirection}`;
        if (sortKey) query += `&sortKey=${sortKey}`;

        const res = await client.getInventories(query);
        setInventories(res.rows);
        setInventoriesCount(res.count);
        setLoading(false);
    }, [page, size, search, sortKey, sortDirection]);

    useEffect(() => {
        getInventories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        getInventories();
    }, [page, size, search, sortKey, sortDirection, getInventories]);

    const completeAddInventory = async () => {
        setShowAddInventory(false);
        await getInventories();
    };

    const handleSearchSubmit = useCallback(() => {}, []);

    // Export configuration
    const formatCurrency = (cents) => {
        if (!cents && cents !== 0) return '';
        return `$${(cents / 100).toFixed(2)}`;
    };

    const exportFn = async () => {
        const res = await client.exportInventories();
        return res.rows || [];
    };

    const formatExportData = (data) => {
        return data.map((inventory) => {
            const createdDate = dayjs(inventory.createdAt).format('MM/DD/YYYY');
            return [
                inventory.id || '',
                inventory.sku || '',
                inventory.Product?.name || '',
                inventory.type || '',
                inventory.size || '',
                inventory.sizeDescription || '',
                formatCurrency(inventory.price),
                inventory.quantity || 0,
                inventory.address || '',
                inventory.bay || '',
                createdDate
            ];
        });
    };

    const exportHeaders = ['ID', 'SKU', 'Product', 'Type', 'Size', 'Size Description', 'Price', 'Quantity', 'Address', 'Bay', 'Created Date'];

    return (
        <>
            <ComponentModal
                isOpen={showAddInventory}
                onClose={() => setShowAddInventory(false)}
                title="Add Inventory"
            >
                <AddInventory onComplete={completeAddInventory} />
            </ComponentModal>

            <TabContainer>
                <TabSelector active={true}>Inventories</TabSelector>
            </TabContainer>

            <ContentContainer>
                <MainTitle>Inventories</MainTitle>

                <TableControls
                    search={search}
                    setSearch={setSearch}
                    onSearchSubmit={handleSearchSubmit}
                    size={size}
                    setSize={setSize}
                    entityName="inventories"
                    exportFn={exportFn}
                    formatExportData={formatExportData}
                    exportHeaders={exportHeaders}
                    additionalButtons={
                        <Button onClick={() => setShowAddInventory(true)}>Add Inventory</Button>
                    }
                    showPageSize={false}
                />

                {loading ? (
                    <Spinner />
                ) : (
                    <>
                        <InventoriesWrapper />

                        <Pagination
                            count={inventoriesCount}
                            size={size}
                            page={page}
                            changePage={setPage}
                        />
                    </>
                )}
            </ContentContainer>
        </>
    );
};

export default InventoriesPage;
